// Simple client-side translation service with cache and offline fallback
// Attempts online translation first (LibreTranslate public endpoint), then falls back to offline/original.

export type SupportedLang = 'en' | 'de' | 'tr';

const CACHE_KEY = 'translation_cache_v1';

interface CacheMap {
  [lang: string]: {
    [text: string]: string;
  };
}

// MyMemory API: https://api.mymemory.translated.net/get?q=TEXT&langpair=SOURCE|TARGET
async function translateWithMyMemoryOnline(text: string, target: SupportedLang, source: string = 'en'): Promise<string | null> {
  try {
    if (!text || target === 'en') return text;
    const encodedQ = encodeURIComponent(text);
    const url = `https://api.mymemory.translated.net/get?q=${encodedQ}&langpair=${encodeURIComponent(source)}|${encodeURIComponent(target)}`;
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const data = await resp.json();
    const translated = data?.responseData?.translatedText as string | undefined;
    if (translated && typeof translated === 'string') return translated;
    return null;
  } catch {
    return null;
  }
}

export async function translateWithMyMemory(text: string, target: SupportedLang, source: string = 'en'): Promise<string> {
  if (!text) return text;
  const cached = getCachedTranslation(`[mymemory] ${text}`, target);
  if (cached) return cached;

  const online = await translateWithMyMemoryOnline(text, target, source);
  const result = online ?? (await translateOnline(text, target, source)) ?? offlineFallback(text, target);
  // Prefix key so it doesn't collide with other engines
  setCachedTranslation(`[mymemory] ${text}`, target, result);
  return result;
}

function loadCache(): CacheMap {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveCache(cache: CacheMap) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {}
}

export function getCachedTranslation(text: string, target: SupportedLang): string | undefined {
  const cache = loadCache();
  return cache?.[target]?.[text];
}

function setCachedTranslation(text: string, target: SupportedLang, translated: string) {
  const cache = loadCache();
  if (!cache[target]) cache[target] = {};
  cache[target][text] = translated;
  saveCache(cache);
}

// Offline lightweight fallback: returns original for EN, or a trivial fallback for DE/TR when API unavailable.
function offlineFallback(text: string, target: SupportedLang): string {
  if (target === 'en') return text;
  // Naive fallback: just return original text; real offline dictionaries could be plugged here.
  return text;
}

async function translateOnline(text: string, target: SupportedLang, source: string = 'auto'): Promise<string | null> {
  try {
    if (!text || target === 'en') return text;

    // 1) Try user-provided API first, if configured
    const customUrl = (import.meta as any)?.env?.VITE_TRANSLATE_API_URL as string | undefined;
    const customKey = (import.meta as any)?.env?.VITE_TRANSLATE_API_KEY as string | undefined;
    if (customUrl) {
      const resp = await fetch(customUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(customKey ? { Authorization: `Bearer ${customKey}` } : {}),
        },
        body: JSON.stringify({ q: text, target, source }),
      });
      if (resp.ok) {
        const data = await resp.json();
        // Accept either { translatedText } or plain string
        const translated = (data?.translatedText ?? data?.translation ?? data) as string | null;
        if (translated && typeof translated === 'string') return translated;
      }
    }

    // 2) Fallback to LibreTranslate public endpoint
    const resp2 = await fetch('https://libretranslate.de/translate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ q: text, source, target, format: 'text' }),
    });
    if (!resp2.ok) return null;
    const data2 = await resp2.json();
    const translated2 = data2?.translatedText || null;
    return translated2;
  } catch {
    return null;
  }
}

export async function translateWithCache(text: string, target: SupportedLang): Promise<string> {
  if (!text) return text;
  const cached = getCachedTranslation(text, target);
  if (cached) return cached;

  const online = await translateOnline(text, target);
  const result = online ?? offlineFallback(text, target);
  setCachedTranslation(text, target, result);
  return result;
}
